import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useT } from "@/lib/i18n";
import { AUTH } from "@/constants/testIds";
import { PixelEvents } from "@/lib/pixel";
import { toast } from "sonner";
import { Flame, ArrowRight } from "lucide-react";
export default function AuthPage() {
    const [params] = useSearchParams();
    const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const { lang, login, register, user } = useApp();
    const t = useT(lang);
    const navigate = useNavigate();

    useEffect(() => { if (user) navigate("/studio", { replace: true }); }, [user, navigate]);

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const signInWithGoogle = () => {
        const redirectUrl = window.location.origin + "/studio";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (mode === "login") {
                await login(email, password);
                toast.success(lang === "fr" ? "Bienvenue !" : "Welcome back!");
            } else {
                await register(email, password, name);
                PixelEvents.lead("register");
                toast.success(lang === "fr" ? "Compte créé." : "Account created.");
            }
            navigate("/studio", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message || "Error";
            toast.error(msg);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#080B10] relative overflow-hidden">
            <div className="absolute inset-0 studio-grid opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#080B10]/60 to-[#080B10]" />

            <div className="relative max-w-md mx-auto px-4 pt-16 pb-24">
                <Link to="/" className="inline-flex items-center gap-2 mb-10">
                    <span className="flame-gradient w-9 h-9 rounded-md grid place-items-center flame-glow">
                        <Flame className="w-4 h-4 text-black" strokeWidth={3} />
                    </span>
                    <span className="font-display text-2xl font-extrabold uppercase">Shorts <span className="flame-text">Factory</span></span>
                </Link>

                <div className="bg-[#161D2A] border border-[#253046] rounded-lg p-8 shadow-2xl">
                    <div className="overline">{mode === "login" ? "Session" : "New creator"}</div>
                    <h1 className="font-display text-3xl font-black uppercase mt-2">
                        {mode === "login" ? t("auth_login") : t("auth_register")}
                    </h1>

                    <div className="mt-6 flex gap-1 p-1 bg-[#0F141C] border border-[#253046] rounded-md">
                        <button
                            data-testid={AUTH.tabLogin}
                            onClick={() => setMode("login")}
                            className={`flex-1 py-2 text-sm rounded font-medium kinetic ${mode === "login" ? "bg-[#FF4D00] text-black" : "text-slate-400 hover:text-white"}`}
                        >{t("signin")}</button>
                        <button
                            data-testid={AUTH.tabRegister}
                            onClick={() => setMode("register")}
                            className={`flex-1 py-2 text-sm rounded font-medium kinetic ${mode === "register" ? "bg-[#FF4D00] text-black" : "text-slate-400 hover:text-white"}`}
                        >{t("create")}</button>
                    </div>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        {mode === "register" && (
                            <div>
                                <label className="text-xs uppercase tracking-widest text-slate-500">{t("name")}</label>
                                <input
                                    data-testid={AUTH.name}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="mt-1 w-full bg-[#0F141C] border border-[#253046] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]"
                                    placeholder="Alex"
                                />
                            </div>
                        )}
                        <div>
                            <label className="text-xs uppercase tracking-widest text-slate-500">{t("email")}</label>
                            <input
                                data-testid={AUTH.email}
                                type="email" required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 w-full bg-[#0F141C] border border-[#253046] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]"
                                placeholder="you@studio.io"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-slate-500">{t("password")}</label>
                            <input
                                data-testid={AUTH.password}
                                type="password" required minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 w-full bg-[#0F141C] border border-[#253046] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]"
                                placeholder="••••••••"
                            />
                        </div>
                        <button
                            data-testid={AUTH.submit}
                            type="submit" disabled={busy}
                            className="w-full flame-gradient text-black font-semibold py-3 rounded-md flex items-center justify-center gap-2 kinetic flame-glow disabled:opacity-60"
                        >
                            {busy ? "···" : (<>{t("submit")} <ArrowRight className="w-4 h-4" /></>)}
                        </button>
                    </form>

                    <div className="my-5 flex items-center gap-3">
                        <div className="flex-1 h-px bg-[#253046]" />
                        <span className="text-[10px] font-mono-sf text-slate-500 uppercase tracking-widest">{lang === "fr" ? "ou" : "or"}</span>
                        <div className="flex-1 h-px bg-[#253046]" />
                    </div>

                    <button
                        data-testid="auth-google-btn"
                        onClick={signInWithGoogle}
                        className="w-full py-3 rounded-md border border-[#253046] hover:border-[#FF4D00] bg-[#0F141C] text-slate-100 hover:text-white font-medium flex items-center justify-center gap-2.5 kinetic"
                    >
                        <svg viewBox="0 0 24 24" className="w-4 h-4">
                            <path fill="#EA4335" d="M5.26 14.29l-.82 3.06-3-.06A9 9 0 0 1 12 3v3.83A5.17 5.17 0 0 0 7.7 12c0 .81.18 1.58.52 2.29z"/>
                            <path fill="#4285F4" d="M20.66 9.75A9 9 0 0 1 12 21a9 9 0 0 1-8.56-6.29l3.82-3.06a5.17 5.17 0 0 0 4.74 3.51 5.14 5.14 0 0 0 3.7-1.55h4.96z"/>
                            <path fill="#FBBC05" d="M15.7 13.61a5.14 5.14 0 0 0 1.3-3.4h-5V6.83h8.66A9 9 0 0 1 20.66 12l-4.96 1.61z"/>
                            <path fill="#34A853" d="M7.7 12l-3.82 3.06A9 9 0 0 1 3 12a9 9 0 0 1 .44-2.77l3.78 2.98A5.17 5.17 0 0 0 7.7 12z"/>
                        </svg>
                        <span>{lang === "fr" ? "Continuer avec Google" : lang === "es" ? "Continuar con Google" : "Continue with Google"}</span>
                    </button>

                    <p className="mt-3 text-[11px] text-slate-500 leading-relaxed text-center" data-testid="auth-oauth-notice">
                        {lang === "fr"
                            ? "Avec « Continuer avec Google », Shorts Factory reçoit uniquement votre email, votre nom et votre photo de profil. Aucun accès à votre chaîne YouTube n'est demandé."
                            : lang === "es"
                            ? "Al continuar con Google, Shorts Factory solo recibe tu email, tu nombre y tu foto de perfil. No se solicita acceso a tu canal de YouTube."
                            : "By continuing with Google, Shorts Factory only receives your email, name and profile picture. No YouTube channel access is requested."}
                    </p>

                    <div className="mt-4 flex justify-center gap-4 text-[10px] font-mono-sf uppercase tracking-widest text-slate-500">
                        <Link to="/privacy" className="hover:text-[#FF4D00] kinetic" data-testid="auth-privacy-link">
                            {lang === "fr" ? "Confidentialité" : lang === "es" ? "Privacidad" : "Privacy"}
                        </Link>
                        <span className="opacity-30">·</span>
                        <Link to="/terms" className="hover:text-[#FF4D00] kinetic" data-testid="auth-terms-link">
                            {lang === "fr" ? "Conditions (brouillon)" : lang === "es" ? "Condiciones (borrador)" : "Terms (draft)"}
                        </Link>
                    </div>

                    <div className="mt-6 text-center text-xs text-slate-500">
                        {mode === "login" ? t("no_account") : t("yes_account")}{" "}
                        <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#FF4D00] hover:underline">
                            {mode === "login" ? t("create") : t("signin")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
